import { Box, Button, styled } from '@mui/material';

import { FileFrame } from '@/components/DataDisplay/File/FileFrame';
import { DocumentDrawer } from '@/components/Layout/Drawer';

const DOT_SIZE = 32;
const LINE_WIDTH = 4;

export const DocumentTimeline = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  marginBottom: theme.spacing(3),
}));

export const DocumentTimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: (DOT_SIZE - LINE_WIDTH) / 2,
  top: DOT_SIZE / 2,
  width: LINE_WIDTH,
  height: `calc(100% - ${theme.spacing(4)})`,
  backgroundColor: theme.palette.neutral[200],
  borderRadius: LINE_WIDTH / 2,
}));

export const DocumentTimelineItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
  minHeight: DOT_SIZE,
  paddingBottom: theme.spacing(2.5),

  '&:last-of-type': {
    paddingBottom: 0,
  },
}));

export const DocumentTimelineDot = styled(Box)(({ theme }) => ({
  width: DOT_SIZE,
  height: DOT_SIZE,
  minWidth: DOT_SIZE,
  borderRadius: '50%',
  border: `2px solid ${theme.palette.neutral[300]}`,
  backgroundColor: theme.palette.neutral[100],
  boxSizing: 'border-box',
}));

export const DocumentLink = styled('button')(({ theme }) => ({
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  textAlign: 'left',
  cursor: 'pointer',
  color: theme.palette.primaries[500],
  fontSize: theme.typography.bodyB2Strong.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.bodyB2Strong.fontWeight,
  lineHeight: theme.typography.bodyB2Strong.lineHeight,
  fontFamily: 'inherit',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const DocumentAgreedTitle = styled('span')(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export const ReviewPrompt = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
}));

export const ReviewSubmitCard = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[1],
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1.5),
}));

export const ReviewSubmitCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const ReviewDocumentDrawerRoot = styled(DocumentDrawer)({
  '& .MuiDrawer-paper': {
    alignItems: 'stretch',
    height: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
  },
});

export const ReviewDocumentBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  width: '100%',
  paddingTop: theme.layout.headerHeight.xs,
  overflow: 'hidden',

  [theme.breakpoints.up('lg')]: {
    paddingTop: 0,
  },
}));

export const ReviewDocumentScrollContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  width: '100%',
});

export const ReviewDocumentScrollArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  width: '100%',

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(3),
  },

  '& .FileThumbnailContainer': {
    borderRadius: theme.shape.borderRadius,
  },
}));

export const ReviewDocumentScrollFader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(transparent, ${theme.palette.background.paper})`,
  position: 'absolute',
  bottom: 0,
  left: 0,
  height: theme.spacing(8),
  width: '100%',
  pointerEvents: 'none',
  zIndex: 1,
}));

export const ReviewDocumentFile = styled(FileFrame)({
  flex: 1,
  minHeight: 0,
  height: 'auto',
});

export const ReviewDocumentActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  width: '100%',
  padding: theme.spacing(2),
  paddingTop: theme.spacing(1),

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1),
  },
}));

export const DownloadDocumentButton = styled(Button)(({ theme }) => ({
  background: theme.palette.primaries[100],
  borderColor: theme.palette.primaries[400],
  color: theme.palette.primaries[500],
  '& .MuiSvgIcon-root': {
    color: theme.palette.primaries[500],
  },
}));
